import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { GET_BASE_URL, GET_BASE_URL_IMAGE } from "../constants";

const STORAGE_KEY = "mw_pending_post_v1";
const MAX_AGE_MS = 5 * 60 * 1000;
const MAX_RESOLVE_ATTEMPTS = 6;

type PendingPost = {
  version: 1;
  clientId: string;
  createdAt: number;
  isWdw: boolean;
  chatRoomId: string;
  chatMsg: string;
  expectImage?: boolean;
  thumbDataUrl?: string;
  phase?: "uploading" | "published" | "resolved" | "failed";
  resolvedChatId?: number;
  resolvedMappingUrl?: string;
  resolvedChatImg?: string;
  resolvedImageUrl?: string;
  error?: string;
};

type LoungeContext = {
  isWdw: boolean;
  chatRoomId: string | null;
};

function safeParsePending(raw: string | null): PendingPost | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return null;
    if (!parsed.clientId || !parsed.createdAt) return null;
    return parsed as PendingPost;
  } catch {
    return null;
  }
}

function getContextFromPath(pathname: string): LoungeContext {
  const isWdw = pathname.startsWith("/disneyworld");

  if (isWdw) {
    if (pathname.startsWith("/disneyworld/lounge")) return { isWdw, chatRoomId: "1" };
    if (pathname.startsWith("/disneyworld/lands/0")) return { isWdw, chatRoomId: "0" };
    if (pathname.startsWith("/disneyworld/lands/2")) return { isWdw, chatRoomId: "2" };
    return { isWdw, chatRoomId: null };
  }

  if (pathname.startsWith("/disneyland/lounge")) return { isWdw, chatRoomId: "7" };
  if (pathname.startsWith("/disneyland/lands/0")) return { isWdw, chatRoomId: "0" };
  if (pathname.startsWith("/disneyland/lands/1")) return { isWdw, chatRoomId: "1" };
  if (pathname.startsWith("/loungeland")) return { isWdw, chatRoomId: "3" };
  if (pathname.startsWith("/club333")) return { isWdw, chatRoomId: "4" };

  return { isWdw, chatRoomId: null };
}

function normalizeText(value: string): string {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function buildDetailHref(isWdw: boolean, chatId: number, mappingUrl?: string): string {
  const base = isWdw ? "/disneyworld/lands-talk/" : "/disneyland/lands-talk/";
  if (mappingUrl) {
    // Mirror existing URL sanitization used across the app.
    const safe = String(mappingUrl).replace(
      /([~!@#$%^&*()_+=`{}\[\]\|\\:;'<>,.\? ])+/g,
      "-"
    );
    return `${base}${safe}`;
  }
  return `${base}${chatId}/Mousewait`;
}

function buildFeedHref(isWdw: boolean, chatRoomId: string): string {
  const id = String(chatRoomId);
  if (isWdw) {
    switch (id) {
      case "0":
        return "/disneyworld/lands/0/the-hub/";
      case "2":
        return "/disneyworld/lands/2/Wdw-Real-Time/";
      case "1":
      default:
        return "/disneyworld/lounge/";
    }
  }

  switch (id) {
    case "0":
      return "/disneyland/lands/0/the-hub/";
    case "1":
      return "/disneyland/lands/1/Disneyland-Real-Time/";
    case "3":
      return "/loungeland/";
    case "4":
      return "/club333/";
    case "7":
    default:
      return "/disneyland/lounge/";
  }
}

function buildFeedLabel(isWdw: boolean, chatRoomId: string): string {
  const id = String(chatRoomId);
  if (isWdw) {
    switch (id) {
      case "0":
        return "WDW Hub";
      case "2":
        return "WDW Real-Time";
      case "1":
      default:
        return "WDW Talk";
    }
  }

  switch (id) {
    case "0":
      return "The Hub";
    case "1":
      return "Disneyland Real-Time";
    case "3":
      return "lounge.land";
    case "4":
      return "Club 333";
    case "7":
    default:
      return "Disneyland Talk";
  }
}

function loadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

async function createThumbFromDataUrl(dataUrl: string, maxSize: number): Promise<string> {
  const ok = await loadImage(dataUrl);
  if (!ok) return "";

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 1;
      const h = img.naturalHeight || 1;
      const scale = Math.min(1, maxSize / Math.max(w, h));
      const outW = Math.max(1, Math.round(w * scale));
      const outH = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve("");
        return;
      }
      ctx.drawImage(img, 0, 0, outW, outH);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => resolve("");
    img.src = dataUrl;
  });
}

async function fetchNewestForRoom(params: {
  isWdw: boolean;
  chatRoomId: string;
  token: string;
  userId: string;
}): Promise<any[]> {
  const { isWdw, chatRoomId, token, userId } = params;
  const sortByPostTime = "sortordefault=ww";

  // Mirrors how the app fetches per-room feeds, but forces "Post Time" sorting
  // to make matching the user's newest post reliable.
  const base = isWdw ? `${GET_BASE_URL}/api/v1/disneyworldHome` : `${GET_BASE_URL}/api/v1/home`;
  const roomPart =
    !isWdw && chatRoomId === "7"
      ? "" // Disneyland Talk uses /home (no /7) in existing code.
      : `/${chatRoomId}`;
  const url = `${base}${roomPart}?page=1&${sortByPostTime}&user_id=${encodeURIComponent(userId)}`;

  const resp = await axios.get(url, {
    headers: { Authorization: `bearer ${token}` },
  });
  return Array.isArray(resp?.data?.data) ? resp.data.data : [];
}

export function PendingPostNotice() {
  const [pending, setPending] = useState<PendingPost | null>(null);
  const resolveAttemptRef = useRef(0);

  const location = useLocation();
  const context = useMemo(
    () => getContextFromPath(location.pathname),
    [location.pathname]
  );

  const dismiss = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    setPending(null);
  };

  const updateStorage = (next: PendingPost) => {
    setPending(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {}
  };

  useEffect(() => {
    const raw = (() => {
      try {
        return sessionStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    })();
    const parsed = safeParsePending(raw);
    if (!parsed) return;

    const isExpired = Date.now() - Number(parsed.createdAt || 0) > MAX_AGE_MS;
    if (isExpired) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {}
      return;
    }
    setPending(parsed);
  }, []);

  useEffect(() => {
    if (!pending) return;
    if (pending.phase === "resolved" || pending.phase === "failed") return;

    const token = localStorage.getItem("token") || "";
    const userId = localStorage.getItem("user_id") || "";
    if (!token || !userId || !pending.chatRoomId) {
      updateStorage({ ...pending, phase: "failed", error: "Not logged in." });
      return;
    }

    let cancelled = false;
    const expectsImage = pending.expectImage !== false;

    const attemptResolve = async () => {
      resolveAttemptRef.current += 1;
      const attempt = resolveAttemptRef.current;

      try {
        const posts = await fetchNewestForRoom({
          isWdw: pending.isWdw,
          chatRoomId: String(pending.chatRoomId),
          token,
          userId,
        });

        const normalizedPending = normalizeText(pending.chatMsg);
        const candidates = posts.filter((p: any) => String(p?.user?.user_id || "") === String(userId));
        const matched =
          candidates.find((p: any) => normalizeText(p?.chat_msg) === normalizedPending) ||
          candidates.find((p: any) => {
            const postTime = p?.chat_time ? new Date(p.chat_time).getTime() : 0;
            return postTime && Math.abs(postTime - pending.createdAt) < 3 * 60 * 1000;
          }) ||
          candidates[0];

        if (matched?.chat_id) {
          const chatId = Number(matched.chat_id);
          const mappingUrl = String(matched.mapping_url || "");
          const chatImg = String(matched.chat_img || "");

          if (!expectsImage) {
            if (cancelled) return;

            updateStorage({
              ...pending,
              phase: "resolved",
              resolvedChatId: chatId,
              resolvedMappingUrl: mappingUrl || undefined,
              resolvedChatImg: chatImg || undefined,
              resolvedImageUrl: undefined,
              error: undefined,
            });
            return;
          }

          const imagePath = pending.isWdw ? "/disneyworld/chat_images/" : "/disneyland/chat_images/";
          const expectedFilename = `${chatId}_c_img.jpg`;
          const bestFilename = chatImg && chatImg.includes("c_img") ? chatImg : expectedFilename;
          const remoteUrl = `${GET_BASE_URL_IMAGE}${imagePath}${bestFilename}?v=${Date.now()}`;

          // If the server image loads, we can confidently tell the user it worked.
          const imageOk = await loadImage(remoteUrl);

          if (cancelled) return;

          updateStorage({
            ...pending,
            phase: "resolved",
            resolvedChatId: chatId,
            resolvedMappingUrl: mappingUrl || undefined,
            resolvedChatImg: chatImg || undefined,
            resolvedImageUrl: imageOk ? remoteUrl : undefined,
            error: imageOk ? undefined : "Image still processing (or failed).",
          });
          return;
        }
      } catch (err) {
        // ignore; retry below
      }

      if (cancelled) return;
      if (attempt >= MAX_RESOLVE_ATTEMPTS) {
        updateStorage({ ...pending, phase: "failed", error: "Could not confirm image upload yet." });
        return;
      }

      const delayMs = 600 + attempt * 550;
      window.setTimeout(attemptResolve, delayMs);
    };

    attemptResolve();

    return () => {
      cancelled = true;
    };
  }, [pending]);

  useEffect(() => {
    const onPendingPost = () => {
      try {
        const parsed = safeParsePending(sessionStorage.getItem(STORAGE_KEY));
        setPending(parsed);
      } catch {
        // ignore
      }
    };
    window.addEventListener("mw:pending-post", onPendingPost);
    return () => window.removeEventListener("mw:pending-post", onPendingPost);
  }, []);

  if (!pending) return null;

  const isResolved = pending.phase === "resolved";
  const isFailed = pending.phase === "failed";
  const expectsImage = pending.expectImage !== false;
  const isPublished = pending.phase === "published";
  const isOnDestinationFeed =
    context.chatRoomId != null &&
    pending.isWdw === context.isWdw &&
    String(pending.chatRoomId) === String(context.chatRoomId);

  const feedHref = buildFeedHref(pending.isWdw, String(pending.chatRoomId));
  const feedLabel = buildFeedLabel(pending.isWdw, String(pending.chatRoomId));

  const title = isResolved
    ? expectsImage && !pending.resolvedImageUrl
      ? "Post published (image pending)"
      : "Post published"
    : isFailed
      ? "Post submitted (needs attention)"
      : isPublished
        ? expectsImage
          ? "Post published (image processing)"
          : "Post published"
        : "Posting…";

  const status = isResolved
    ? expectsImage
      ? pending.resolvedImageUrl
        ? isOnDestinationFeed
          ? "Post is live. Image attached."
          : `Post is live in ${feedLabel}.`
        : `Post is live in ${feedLabel}. Image may take a moment to appear.`
      : `Post is live in ${feedLabel}.`
    : isFailed
      ? pending.error ||
        (expectsImage
          ? `Post is live in ${feedLabel}, but we couldn’t confirm the image yet.`
          : `Post is live in ${feedLabel}, but we couldn’t confirm it yet.`)
      : isOnDestinationFeed
        ? isPublished
          ? expectsImage
            ? "Posted. Image is processing. You can keep scrolling and commenting."
            : "Posted. You can keep scrolling and commenting."
          : "Uploading… you can keep scrolling and commenting."
        : isPublished
          ? expectsImage
            ? `Posted to ${feedLabel}. Image is processing.`
            : `Posted to ${feedLabel}.`
          : `Uploading… post is headed to ${feedLabel}.`;

  const href =
    pending.resolvedChatId != null
      ? buildDetailHref(pending.isWdw, pending.resolvedChatId, pending.resolvedMappingUrl)
      : undefined;

  return (
    <div className="pending-post-notice" role="status" aria-live="polite">
      {pending.resolvedImageUrl || pending.thumbDataUrl ? (
        <img
          className="pending-post-notice__thumb"
          src={pending.resolvedImageUrl || pending.thumbDataUrl}
          alt="Posting preview"
          loading="eager"
        />
      ) : (
        <div className="pending-post-notice__thumb pending-post-notice__thumb--empty" />
      )}
      <div className="pending-post-notice__meta">
        <div className="pending-post-notice__title">
          {!isResolved && !isFailed && <span className="pending-post-notice__spinner" />}
          {title}
        </div>
        <div className="pending-post-notice__status">{status}</div>
        <div className="pending-post-notice__caption">
          {String(pending.chatMsg || "")
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 160)}
        </div>
        <div className="pending-post-notice__actions">
          {href ? (
            <a className="pending-post-notice__btn pending-post-notice__btn--primary" href={href}>
              View Post
            </a>
          ) : (
            <a className="pending-post-notice__btn pending-post-notice__btn--primary" href={feedHref}>
              Go to {feedLabel}
            </a>
          )}
          <button className="pending-post-notice__btn" type="button" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export async function writePendingPost(params: {
  isWdw: boolean;
  chatRoomId: string;
  chatMsg: string;
  expectImage?: boolean;
  imagePreviewDataUrl?: string;
}) {
  const clientId = `pending_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const base: PendingPost = {
    version: 1,
    clientId,
    createdAt: Date.now(),
    isWdw: params.isWdw,
    chatRoomId: params.chatRoomId,
    chatMsg: params.chatMsg,
    expectImage: params.expectImage ?? !!params.imagePreviewDataUrl,
    phase: "uploading",
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(base));
    try {
      window.dispatchEvent(new Event("mw:pending-post"));
    } catch {}
  } catch {
    return;
  }

  if (params.imagePreviewDataUrl) {
    // Generate a small thumb so it survives reload without huge storage.
    try {
      const thumb = await createThumbFromDataUrl(params.imagePreviewDataUrl, 320);
      if (!thumb) return;

      const raw = safeParsePending(sessionStorage.getItem(STORAGE_KEY));
      if (!raw || raw.clientId !== clientId) return;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...raw, thumbDataUrl: thumb }));
      try {
        window.dispatchEvent(new Event("mw:pending-post"));
      } catch {}
    } catch {
      // ignore
    }
  }
}

export function markPendingPostPublished() {
  try {
    const raw = safeParsePending(sessionStorage.getItem(STORAGE_KEY));
    if (!raw) return;

    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...raw,
        phase: "published",
        error: undefined,
      })
    );
    window.dispatchEvent(new Event("mw:pending-post"));
  } catch {
    // ignore
  }
}
